import { useMemo, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

const SLOT_H = 48;        // altezza di mezz'ora
const SNAP_MIN = 15;      // lo spostamento si aggancia al quarto d'ora
const HOLD_MS = 300;      // su touch il trascinamento parte dopo una pressione prolungata

const minutesOf = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

const label = (totalMin) => `${String(Math.floor(totalMin / 60)).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}`;

/** Agenda del giorno: ore in riga, una colonna per operatore. */
export const DayGrid = ({
  date, staff, appointments, openTime, closeTime, closed, closedReason, busy = [],
  onSlotClick, onAppointmentClick, onMove, getStaffColor,
}) => {
  const gridRef = useRef(null);
  const dragRef = useRef(null);
  const holdRef = useRef(null);
  const [drag, setDrag] = useState(null);   // { id, staffId, startMin, durationMin }

  const dayAppointments = useMemo(
    () => appointments.filter(a => a.appointment_status !== 'cancelled'),
    [appointments]
  );

  // Le chiusure di tutto il salone valgono per ogni colonna
  const columnBusy = (staffId) => busy.filter(b => !b.staff_id || b.staff_id === staffId);

  // La griglia copre gli orari di apertura, allargati se un appuntamento cade fuori
  const [fromMin, toMin] = useMemo(() => {
    let start = minutesOf(openTime);
    let end = minutesOf(closeTime);
    dayAppointments.forEach((a) => {
      const s = parseISO(a.start_time);
      const e = a.end_time ? parseISO(a.end_time) : s;
      start = Math.min(start, Math.floor((s.getHours() * 60 + s.getMinutes()) / 30) * 30);
      end = Math.max(end, Math.ceil((e.getHours() * 60 + e.getMinutes()) / 30) * 30);
    });
    return [start, Math.max(end, start + 60)];
  }, [openTime, closeTime, dayAppointments]);

  const rows = useMemo(() => {
    const out = [];
    for (let m = fromMin; m < toMin; m += 30) out.push(m);
    return out;
  }, [fromMin, toMin]);

  const nowMin = (() => {
    const now = new Date();
    if (format(now, 'yyyy-MM-dd') !== format(date, 'yyyy-MM-dd')) return null;
    const m = now.getHours() * 60 + now.getMinutes();
    return m >= fromMin && m <= toMin ? m : null;
  })();

  const atMinutes = (totalMin) => {
    const d = new Date(date);
    d.setHours(Math.floor(totalMin / 60), totalMin % 60, 0, 0);
    return d;
  };

  const pointFromEvent = (e) => {
    const point = e.touches?.[0] || e.changedTouches?.[0] || e;
    return { x: point.clientX, y: point.clientY };
  };

  const resolveDrop = (e) => {
    const { x, y } = pointFromEvent(e);
    const column = document.elementFromPoint(x, y)?.closest('[data-staff-column]');
    const box = gridRef.current?.getBoundingClientRect();
    if (!box) return null;
    const offset = Math.max(0, y - box.top);
    const raw = fromMin + (offset / SLOT_H) * 30;
    const snapped = Math.round(raw / SNAP_MIN) * SNAP_MIN;
    return {
      staffId: column?.getAttribute('data-staff-column') || null,
      startMin: Math.min(Math.max(snapped, fromMin), toMin - SNAP_MIN),
    };
  };

  const endDrag = (e) => {
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
    clearTimeout(holdRef.current);
    const info = dragRef.current;
    dragRef.current = null;
    setDrag(null);
    if (!info?.active) return;
    const drop = resolveDrop(e);
    if (!drop) return;
    const movedStaff = drop.staffId && drop.staffId !== info.staffId;
    if (!movedStaff && drop.startMin === info.startMin) return;
    onMove(info.appointment, drop.staffId || info.staffId, atMinutes(drop.startMin), info.durationMin);
  };

  const onPointerMove = (e) => {
    const info = dragRef.current;
    if (!info) return;
    const { x, y } = pointFromEvent(e);
    if (!info.active && Math.abs(y - info.originY) < 6 && Math.abs(x - info.originX) < 6) return;
    if (!info.active) {
      if (info.pointerType === 'touch') return;   // su touch serve la pressione prolungata
      info.active = true;
    }
    const drop = resolveDrop(e);
    if (drop) setDrag({ id: info.appointment.id, staffId: drop.staffId || info.staffId, startMin: drop.startMin, durationMin: info.durationMin });
  };

  const startDrag = (e, appointment, startMin, durationMin) => {
    if (e.button === 2) return;
    dragRef.current = {
      appointment, staffId: appointment.staff_id, startMin, durationMin,
      originX: e.clientX, originY: e.clientY, pointerType: e.pointerType, active: e.pointerType === 'mouse' ? false : false,
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
    if (e.pointerType !== 'mouse') {
      holdRef.current = setTimeout(() => {
        if (dragRef.current) {
          dragRef.current.active = true;
          setDrag({ id: appointment.id, staffId: appointment.staff_id, startMin, durationMin });
          if (navigator.vibrate) navigator.vibrate(12);
        }
      }, HOLD_MS);
    }
  };

  if (!staff.length) {
    return (
      <div className="rounded-xl border border-dashed p-10 text-center" data-testid="day-grid-empty">
        <p className="text-sm text-muted-foreground">
          Aggiungi almeno un operatore per vedere l’agenda del giorno.
        </p>
      </div>
    );
  }

  const columnWidth = staff.length <= 3 ? 'minmax(160px, 1fr)' : 'minmax(140px, 1fr)';

  return (
    <div className="overflow-x-auto rounded-xl border bg-card" data-testid="day-grid">
      {closed && (
        <p className="border-b bg-muted/50 px-4 py-2 text-xs text-muted-foreground" data-testid="day-grid-closed">
          {closedReason || 'Salone chiuso'}: in questo giorno l’agenda non accetta nuovi appuntamenti.
        </p>
      )}
      <div className="min-w-fit">
        {/* Intestazione operatori */}
        <div
          className="sticky top-0 z-20 grid border-b bg-card/95 backdrop-blur"
          style={{ gridTemplateColumns: `64px repeat(${staff.length}, ${columnWidth})` }}
        >
          <div className="px-2 py-2.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {format(date, 'EEE d', { locale: it })}
          </div>
          {staff.map((member) => (
            <div key={member.id} className="flex items-center gap-2 border-l px-3 py-2.5" data-testid={`day-grid-head-${member.id}`}>
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: getStaffColor(member.id) }} />
              <span className="truncate text-sm font-medium">{member.name}</span>
            </div>
          ))}
        </div>

        {/* Griglia */}
        <div className="pt-3">
        <div
          ref={gridRef}
          className="relative grid"
          style={{ gridTemplateColumns: `64px repeat(${staff.length}, ${columnWidth})` }}
        >
          {/* Colonna orari */}
          <div>
            {rows.map((m) => (
              <div key={m} className="relative border-b border-border/50" style={{ height: SLOT_H }}>
                <span className="absolute -top-2 right-2 bg-card px-1 text-[11px] tabular-nums text-muted-foreground">
                  {label(m)}
                </span>
              </div>
            ))}
          </div>

          {/* Una colonna per operatore */}
          {staff.map((member) => {
            const items = dayAppointments.filter(a => a.staff_id === member.id);
            return (
              <div key={member.id} className="relative border-l" data-staff-column={member.id}>
                {rows.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => onSlotClick(member.id, atMinutes(m))}
                    className="block w-full border-b border-border/50 transition-colors hover:bg-primary/5"
                    style={{ height: SLOT_H }}
                    aria-label={`Nuovo appuntamento ${label(m)} con ${member.name}`}
                    data-testid={`day-slot-${member.id}-${label(m).replace(':', '')}`}
                  />
                ))}

                {columnBusy(member.id).map((b, i) => {
                  const bs = parseISO(b.start);
                  const be = parseISO(b.end);
                  const sMin = bs.getHours() * 60 + bs.getMinutes();
                  const eMin = be.getHours() * 60 + be.getMinutes();
                  return (
                    <div
                      key={`busy-${i}`}
                      className="pointer-events-none absolute left-0 right-0 border-y border-muted-foreground/20 bg-[repeating-linear-gradient(45deg,transparent,transparent_6px,rgba(120,120,120,0.18)_6px,rgba(120,120,120,0.18)_12px)]"
                      style={{
                        top: ((Math.max(sMin, fromMin) - fromMin) / 30) * SLOT_H,
                        height: ((Math.min(eMin, toMin) - Math.max(sMin, fromMin)) / 30) * SLOT_H,
                      }}
                      title={b.reason || 'Impegno personale'}
                      data-testid={`day-busy-${member.id}-${i}`}
                    >
                      <span className="ml-1 text-[10px] text-muted-foreground">{b.reason || 'Occupato'}</span>
                    </div>
                  );
                })}

                {items.map((apt) => {
                  const start = parseISO(apt.start_time);
                  const end = apt.end_time ? parseISO(apt.end_time) : start;
                  const startMin = start.getHours() * 60 + start.getMinutes();
                  const durationMin = Math.max(Math.round((end - start) / 60000) || 30, 15);
                  const isDragging = drag?.id === apt.id;
                  const shownStart = isDragging ? drag.startMin : startMin;
                  const shownHere = isDragging ? drag.staffId === member.id : true;
                  if (!shownHere) return null;
                  return (
                    <div
                      key={apt.id}
                      onPointerDown={(e) => startDrag(e, apt, startMin, durationMin)}
                      onClick={() => { if (!dragRef.current?.active) onAppointmentClick(apt); }}
                      className={`absolute left-1 right-1 overflow-hidden rounded-lg border-l-4 bg-background/95 px-2 py-1 text-left shadow-sm transition-shadow hover:shadow-md ${
                        isDragging ? 'z-30 cursor-grabbing opacity-90 shadow-lg ring-2 ring-primary' : 'cursor-grab'
                      }`}
                      style={{
                        top: ((shownStart - fromMin) / 30) * SLOT_H + 2,
                        height: (durationMin / 30) * SLOT_H - 4,
                        borderLeftColor: getStaffColor(apt.staff_id),
                        touchAction: isDragging ? 'none' : 'pan-y',
                      }}
                      data-testid={`day-appointment-${apt.id}`}
                    >
                      <p className="truncate text-xs font-semibold leading-tight">{apt.customer_name || 'Cliente'}</p>
                      <p className="truncate text-[11px] leading-tight text-muted-foreground">{apt.service_name}</p>
                      <p className="text-[10px] tabular-nums text-muted-foreground">
                        {label(shownStart)}–{label(shownStart + durationMin)}
                      </p>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Linea dell'ora attuale */}
          {nowMin !== null && (
            <div
              className="pointer-events-none absolute left-0 right-0 z-10 flex items-center"
              style={{ top: ((nowMin - fromMin) / 30) * SLOT_H }}
              data-testid="day-grid-now"
            >
              <span className="ml-14 h-1.5 w-1.5 rounded-full bg-red-500" />
              <span className="h-px flex-1 bg-red-500/70" />
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};
